import React, { useMemo, useCallback } from 'react';
import { useQuery, useQueryClient} from 'react-query';
import { useChannel, useEvent } from '@harelpls/use-pusher';

import { fetchContract } from 'api/actions/contracts';
import { fetcher } from 'api/client';
import { useContractListState } from 'context/contracts';
import { useProfileAbility } from 'context/profile';

import ItemDetail from 'components/Shared/ItemDetail';
import { 
  Timeline,
  Documents,
  Properties,
  Voting,
  Collaborators,
  Amount,
  Milestones,
  Blockchain,
  MedicalPrescriptions
} from './Contents'

const initialValues = {
    name: 'ContractName'
}

const tabs = [
    { 
        name: 'timeline', 
        icon: 'stream', 
        Component: Timeline,
        tooltip: ({ t }) => t('contracts.timeline.title')
    },
    { 
        name: 'blockchain', 
        brand: 'ethereum',
        Component: Blockchain,
        tooltip: ({ t }) => t('contracts.timeline.blockchain'),
        shouldShow: ({ contract, profileAbility }) => {
            return !!contract?._address || !!contract?.isPagare
        }
    },
    { 
        name: 'documents', 
        icon: 'folder', 
        Component: Documents,
        tooltip: ({ t }) => t('contracts.documents.title')
    },
    { 
        name: 'properties', 
        icon: 'cog', 
        Component: Properties,
        tooltip: ({ t }) => t('records.sidebar.properties'),
        shouldShow: ({ contract, profileAbility }) => {
            return profileAbility.can('read', 'Contract', 'properties');
        }
    },
    { 
        name: 'collaborators', 
        icon: 'users', 
        Component: Collaborators ,
        tooltip: ({ t }) => t('contracts.collaborators.title'),
        shouldShow: ({ contract, profileAbility }) => {
            return profileAbility.can('read', 'Contract', 'collaboration');
        }
    },
    { 
        name: 'amount', 
        icon: 'hand-holding-usd', 
        Component: Amount,
        tooltip: ({ t }) => t('contracts.payments.title'),
        shouldShow: ({ contract, profileAbility }) => {
            return profileAbility.can('read', 'Contract', 'payments');
        }
    },
    { 
        name: 'milestones', 
        icon: 'check', 
        Component: Milestones,
        tooltip: ({ t }) => t('contracts.milestones.title'),
        shouldShow: ({ contract, profileAbility }) => {
            return profileAbility.can('read', 'Contract', 'milestones');
        }
    },
    { 
        name: 'voting', 
        icon: 'person-booth', 
        Component: Voting,
        tooltip: ({ t }) => t('voting.voting'),
        shouldShow: ({ contract }) => {
            return !!contract?.isVotable
        } 
    },
    { 
        name: 'medical-prescriptions', 
        icon: 'notes-medical', 
        Component:  MedicalPrescriptions,
        tooltip: ({ t }) => t('Recetas Medicas'),
        shouldShow: ({ contract, profileAbility }) => {
            return profileAbility.can('modify', contract) && !!contract?.isMedicalPrescription && profileAbility.can('SET_MEDICAL_PRESCRIPTION'); 
        } 
    },
    // { name: 'audit', icon: 'binoculars' },
]



function ContractDetail({ isOpen,  toggle, initialContract = initialValues, onRefreshList }) {
    const contractQueryId = useMemo(() => {
        return ['contract', initialContract._id]
    }, [initialContract])

    const queryClient = useQueryClient();
    const ability = useProfileAbility()
    const { data, status } = useQuery(contractQueryId, ({ queryKey: [, contractId] }) => fetcher(fetchContract(contractId)), {
        enabled: !!initialContract._id && isOpen,
        initialData: initialContract,
        staleTime: 0
    })
    const refetch = useCallback(() =>{
         queryClient.invalidateQueries(contractQueryId)
         queryClient.invalidateQueries('contracts-questionnaire')
        }
    , [queryClient, contractQueryId])
    const channel = useChannel(initialContract?._id ? `private-${initialContract._id}` : null);

    useEvent(channel, 'contractUpdate', () => {
        refetch()
    });

    return ( 
        <ItemDetail 
            isOpen={isOpen}
            title={data && `${data.documentId} - ${data.name}`}
            onRefresh={refetch}
            onClose={toggle}
            tabs={tabs}
            defaultTab="timeline"
            contract={data}
            contractQueryId={contractQueryId}
            refetch={refetch}
            refetchList={onRefreshList}
            profileAbility={ability}
        />  
    )
}

function ContractDetailContainer(props) {
    // if (!props.isOpen) {
    //     return null;
    // }

    return (<ContractDetail {...props} />)
}

export default ContractDetailContainer;